/**
 * src/domain/media/media-classifier.ts
 *
 * Real-time, zero-latency client classifier that inspects text or URLs and extracts:
 * - Media type (video_embed, whiteboard, presentation, document_pdf, animation_lottie, etc.)
 * - Provider & Platform IDs (YouTube, Vimeo)
 * - Embed URLs formatted for privacy-first rendering (youtube-nocookie.com)
 */

import type { MediaClassifierResult, MediaType } from './media-feed-contracts.js';

export class MediaClassifier {
  public static classifyUrl(url: string): MediaClassifierResult {
    return this.classify(url);
  }

  /**
   * Classifies an input string or pasted URL into a structured MediaClassifierResult.
   */
  public static classify(input: string): MediaClassifierResult {
    const raw = (input || '').trim();
    if (!raw) {
      return {
        type: 'text_pulse',
        confidence: 0.0,
        meta: {},
      };
    }

    // 1. YouTube Detection
    const ytMatch = this.extractYouTubeId(raw);
    if (ytMatch) {
      const isLive = /youtube\.com\/live/i.test(raw) || ytMatch.isLive;
      return {
        type: 'video_embed',
        confidence: 1.0,
        suggestedTitle: isLive ? 'Live Stream Broadcast' : 'Video Session',
        meta: {
          provider: 'youtube',
          platformId: ytMatch.id,
          embedUrl: `https://www.youtube-nocookie.com/embed/${ytMatch.id}?rel=0&modestbranding=1`,
          isLive,
        },
      };
    }

    // 2. Vimeo Detection
    const vimeoId = this.extractVimeoId(raw);
    if (vimeoId) {
      return {
        type: 'video_embed',
        confidence: 1.0,
        suggestedTitle: 'Vimeo Video Session',
        meta: {
          provider: 'vimeo',
          platformId: vimeoId,
          embedUrl: `https://player.vimeo.com/video/${vimeoId}`,
          isLive: false,
        },
      };
    }

    // 3. Collaborative Mesh Whiteboard
    if (/^hts:\/\/[^/]+\/whiteboard/i.test(raw) || raw.startsWith('whiteboard:') || raw.startsWith('mesh-whiteboard:')) {
      const room = raw.replace(/^mesh-whiteboard:/, 'hts://mesh/whiteboard/').replace(/^whiteboard:/, 'hts://mesh/whiteboard/');
      return {
        type: 'whiteboard',
        confidence: 1.0,
        suggestedTitle: 'Collaborative Whiteboard Canvas',
        meta: {
          roomUri: room,
        },
      };
    }

    // 4. PDF Document / Technical Brief
    if (/\.pdf($|\?)/i.test(raw)) {
      return {
        type: 'document_pdf',
        confidence: 0.95,
        suggestedTitle: 'PDF Datasheet / Technical Brief',
        meta: {
          fileUrl: raw,
        },
      };
    }

    // 5. Lottie Animation
    if (/\.lottie($|\?)/i.test(raw) || (/\.json($|\?)/i.test(raw) && raw.toLowerCase().includes('lottie'))) {
      return {
        type: 'animation_lottie',
        confidence: 0.9,
        suggestedTitle: 'Interactive Motion Canvas',
        meta: {
          fileUrl: raw,
        },
      };
    }

    // 6. Presentation / Slide Deck
    if (/slides|presentation|speakerdeck\.com|slideshare\.net/i.test(raw) || /\.(pptx?|key)($|\?)/i.test(raw)) {
      return {
        type: 'presentation',
        confidence: 0.85,
        suggestedTitle: 'Interactive Slide Presentation',
        meta: {
          fileUrl: raw,
        },
      };
    }

    // 7. Image Gallery
    if (/\.(png|jpe?g|gif|webp|svg)($|\?)/i.test(raw)) {
      return {
        type: 'image_gallery',
        confidence: 0.95,
        suggestedTitle: 'Visual Asset',
        meta: {
          fileUrl: raw,
        },
      };
    }

    // 8. Default: Sovereign Text Pulse
    return {
      type: 'text_pulse',
      confidence: 0.5,
      suggestedTitle: 'Sovereign Pulse',
      meta: {},
    };
  }

  /**
   * Extracts YouTube Video ID from any valid YouTube URL variation.
   */
  public static extractYouTubeId(url: string): { id: string; isLive: boolean } | null {
    if (!url || typeof url !== 'string') return null;

    // Standard watch URL: youtube.com/watch?v=XXXXX
    const watchMatch = url.match(/(?:youtube\.com\/watch\?(?:.*&)?v=)([^&?/]+)/i);
    if (watchMatch && watchMatch[1]) {
      return { id: watchMatch[1], isLive: false };
    }

    // Shortened URL: youtu.be/XXXXX
    const shortMatch = url.match(/(?:youtu\.be\/)([^&?/]+)/i);
    if (shortMatch && shortMatch[1]) {
      return { id: shortMatch[1], isLive: false };
    }

    // Live Stream: youtube.com/live/XXXXX
    const liveMatch = url.match(/(?:youtube\.com\/live\/)([^&?/]+)/i);
    if (liveMatch && liveMatch[1]) {
      return { id: liveMatch[1], isLive: true };
    }

    // Shorts URL: youtube.com/shorts/XXXXX
    const shortsMatch = url.match(/(?:youtube\.com\/shorts\/)([^&?/]+)/i);
    if (shortsMatch && shortsMatch[1]) {
      return { id: shortsMatch[1], isLive: false };
    }

    // Direct Embed: youtube.com/embed/XXXXX or youtube-nocookie.com/embed/XXXXX
    const embedMatch = url.match(/(?:youtube(?:-nocookie)?\.com\/embed\/)([^&?/]+)/i);
    if (embedMatch && embedMatch[1]) {
      return { id: embedMatch[1], isLive: false };
    }

    return null;
  }

  /**
   * Extracts Vimeo video ID from any valid Vimeo URL.
   */
  public static extractVimeoId(url: string): string | null {
    if (!url || typeof url !== 'string') return null;
    const match = url.match(/vimeo(?:\.com\/video\/|\.com\/)(\d+)/i);
    return match ? match[1] : null;
  }
}
